import { Suspense, useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { authRouteHref, viewReturnAddress } from '@/features/accounts/lib/authRouteHref';
import {
  navOptionClass,
  navOptionDividerClass,
  userAreaClass,
  signInPillClass,
  helpLinkClass,
} from './appshell.css';
import {
  headerBarRightPadding,
  headerRightSectionPadding,
  helpLinkMargins,
  signInPillStyle,
} from './headerGeometry';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useProfileDialogStore } from '@/store';
import { UserMenu } from '../UserMenu';
import { NotificationBell } from '../NotificationBell';
import { ProfileEditDialog } from '@/features/profile/components';
import { useUserAvatarView } from '@/features/profile/pendingAvatar';
import { ForcedContextSelectionModal } from '@/features/context';
// Imported from its module (not the @/features/context barrel) so component
// tests that mock the barrel still render the real, harmless closed dialog.
import { ContextSettingsDialog } from '@/features/context/components/ContextSettingsDialog';
import { useUploadQueueProcessor } from '@/modules/upload-queue';
import { useBranding } from '@/hooks/useBranding';
import { useIsMobile } from '@/hooks/useIsMobile';
import { isNoCompanyLogin } from '@/lib/defaultRoute';
import { testIds } from '@/test/testIds';
import { buttonLabels, paths } from '@/constants';
import { SPINNER_SIZE, LoadingZoneProvider } from '@/components/ui/LoadingZone';
import { FeedbackZone } from '@/components/ui/FeedbackZone';
import { Avatar, Drawer, Spinner } from '@/components/primitives';
import { RouteResettingErrorBoundary } from '@/components/errors';
import { useCtxGateStore } from '@/features/context/ctxGateStore';
import { Icons, moduleIcons, vars, zIndex, shadows, breakpoints } from '@/theme';

/** Navigation option configuration */
interface NavOption {
  name: string;
  title: string;
  icon: React.ComponentType<{ sx?: React.CSSProperties; style?: React.CSSProperties }>;
  route: string;
  accessCheck: () => boolean;
}

/**
 * Which surface the shell is framing.
 *
 * - `app` (default): the authenticated application. Context-scoped chrome
 *   (notifications, the forced context chooser) belongs here.
 * - `consume`: the public learner surface (`/view/:stub`). A learner following
 *   a share link has NO org/network context and can never acquire one there, so
 *   nothing context-scoped may mount: every such request is a guaranteed 400
 *   ("No organization or network context selected" — vingreact#64 / prod
 *   condition 28) and a context chooser would be a dead end over content the
 *   learner is entitled to see.
 */
export type AppShellSurface = 'app' | 'consume';

export interface AppShellProps {
  surface?: AppShellSurface;
}

/**
 * AppShell - Main application shell with header, navigation, and content area
 *
 * Provides the consistent layout for all authenticated pages including:
 * - Logo with dashboard navigation
 * - Navigation icons (Dashboard, Contacts, Packets, Checklists) for desktop/admin-manager
 * - User area with display name, avatar, and dropdown menu
 * - Help link to support site
 * - Content area with React Router outlet
 *
 * Reference: .claude/docs/specs/components/layout/AppShell.md
 */
export function AppShell({ surface = 'app' }: AppShellProps = {}) {
  const isConsumeSurface = surface === 'consume';
  // True-mobile (767) — drives the compact header/hamburger nav. Distinct
  // from `isNarrowViewport` below (900) used for the content area's overflow
  // floor: a narrow-but-not-phone desktop window still gets the full header
  // and nav, just with less room, so these two concerns must not share a
  // threshold.
  const isMobile = useIsMobile();
  const isNarrowViewport = useIsMobile(breakpoints.desktopWidth);
  const navigate = useNavigate();
  const location = useLocation();

  const {
    user,
    isViewer,
    isStaff,
    isSuperuser,
    currentContext,
    canSwitchContext,
    requiresContextSelection,
  } = useCurrentUser();

  // Initialize upload queue processor at app level
  useUploadQueueProcessor();

  // Suppress the forced context chooser while the ctx-param gate is active (#404).
  const ctxGatePhase = useCtxGateStore((s) => s.phase);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  // A page (the dashboard's empty To Do, #1400) asks for THIS dialog rather
  // than rendering a second one.
  const addMethodRequested = useProfileDialogStore((s) => s.addMethodRequested);
  useEffect(() => {
    if (addMethodRequested) setIsProfileOpen(true);
  }, [addMethodRequested]);
  const [isContextSettingsOpen, setIsContextSettingsOpen] = useState(false);
  const [isNavDrawerOpen, setIsNavDrawerOpen] = useState(false);

  // Determine header visibility - hidden on login pages only
  const shouldShowHeader = !location.pathname.startsWith('/login');

  // #1111 (1111b): the header's way to sign in (logo while signed out, Sign In
  // link). On a course page it carries the course as the sign-in redirect;
  // anywhere else it is `/login`, as before.
  const signInHref = authRouteHref(paths.login, viewReturnAddress(`${location.pathname}${location.search}`));
  // The Sign In link's rendered href never carries the course: a course URL
  // holds the recipient-link segment and `?claim=`, which must reach no DOM
  // attribute (vingreact#717). A plain click navigates to `signInHref`; a
  // modified click (new tab / window) opens the bare sign-in page.
  const signInLinkHref = authRouteHref(paths.login, new URLSearchParams());
  const handleSignInLinkClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    void navigate(signInHref);
  };

  // Check if user is authenticated
  const isAuthenticated = !!user;

  // Navigation options configuration
  const navOptions: NavOption[] = [
    {
      name: 'dashboard',
      title: 'Dashboard',
      icon: moduleIcons.dashboard,
      route: '/',
      accessCheck: () => true, // Always visible for admin/manager
    },
    {
      name: 'analytics',
      title: 'Analytics',
      icon: moduleIcons.analytics,
      route: '/analytics',
      // Legacy parity: nav is NEVER hidden by org/network feature flags. Those
      // flags only gate sharing + viewing (enforced server-side) and surface an
      // upgrade notice — they don't remove the area. Role gating (viewer) is
      // handled separately by `showNavigation`.
      accessCheck: () => true,
    },
    {
      name: 'contacts',
      title: 'Contacts',
      icon: moduleIcons.contacts,
      route: paths.contacts,
      accessCheck: () => true,
    },
    {
      name: 'packets',
      title: 'Packets',
      icon: moduleIcons.packets,
      route: paths.packets,
      accessCheck: () => true,
    },
    {
      name: 'checklists',
      title: 'Checklists',
      icon: moduleIcons.checklists,
      route: paths.checklists,
      accessCheck: () => true,
    },
  ];

  // Check if navigation should be shown (authenticated + desktop + non-viewer role)
  // #1400: a login that belongs to no company has no manager destinations
  // (decision Q-353): hide by the predicate, not by level (its level is null).
  const noCompany = !!user && isNoCompanyLogin(user);
  const showNavigation = isAuthenticated && !isMobile && !isViewer && !noCompany;
  // Mobile equivalent: same audience, reached via the hamburger drawer instead
  // of inline header icons (there's no room for them in the mobile header).
  const showMobileNavToggle = isAuthenticated && isMobile && !isViewer && !noCompany;

  // Get currently selected nav option from URL
  const getSelectedOption = useCallback(() => {
    const path = location.pathname;
    // Root path is admin dashboard
    if (path === '/') {
      return 'dashboard';
    }
    for (const option of navOptions) {
      if (option.route !== '/' && path.startsWith(option.route)) {
        return option.name;
      }
    }
    return '';
  // eslint-disable-next-line react-hooks/exhaustive-deps -- navOptions is an inline array recreated each render; adding it would cause infinite re-renders
  }, [location.pathname]);

  // Handle scroll to add 'scrolled' class to body
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY >= 65) {
        document.body.classList.add('scrolled');
      } else {
        document.body.classList.remove('scrolled');
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
      // Always strip the class on unmount — otherwise a scrolled state leaks
      // into the next mounted shell (e.g. the login layout after logout).
      document.body.classList.remove('scrolled');
    };
  }, []);

  // Set view attribute on document element for current page slug
  useEffect(() => {
    const slug = location.pathname.split('/')[1] || 'root';
    document.documentElement.setAttribute('view', slug);
    return () => {
      document.documentElement.removeAttribute('view');
    };
  }, [location.pathname]);

  // Get user display name
  const getUserDisplayName = () => {
    if (!user) return '';
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user.firstName || user.lastName || user.emails?.[0] || '';
  };

  // The header avatar (#885 D4/D6): the server FileRef resolved through the
  // file layer, with this session's just-saved pick as its local echo — the
  // pick while it processes, the rendition once ready, and initials (no src)
  // for a failed or absent avatar.
  const avatarView = useUserAvatarView(user);
  const getUserAvatar = () => {
    return avatarView.url || undefined;
  };

  // Get user initials for avatar fallback
  const getUserInitials = () => {
    if (!user) return '';
    const first = user.firstName?.[0] || '';
    const last = user.lastName?.[0] || '';
    if (first || last) return (first + last).toUpperCase();
    return user.emails?.[0]?.[0]?.toUpperCase() || '';
  };

  // Handle logo click - unauthenticated go to login, viewers go to their dashboard, others go to admin dashboard
  const handleLogoClick = () => {
    if (!isAuthenticated) {
      void navigate(signInHref);
    } else {
      void navigate(isViewer || noCompany ? paths.dashboard : paths.root);
    }
  };

  // Handle nav option click
  const handleNavClick = (route: string) => {
    void navigate(route);
    setIsNavDrawerOpen(false);
  };

  // Network white-label branding for the header logo (default Ving when none).
  const { headerLogoSrc } = useBranding();

  // Desktop dimensions
  const headerHeight = isMobile ? 30 : 50;
  const logoMarginY = isMobile ? 2 : 9;
  // Constrain the header logo by HEIGHT (fill the bar minus its vertical
  // margins) rather than width — a per-network branding logo can be any aspect
  // ratio, and width-constraining let a tall/square one overflow the bar.
  // width:auto + object-fit:contain keeps the aspect; maxWidth stops an
  // ultra-wide logo from shoving the nav.
  const logoHeight = headerHeight - logoMarginY * 2;
  const logoMaxWidth = isMobile ? 120 : 200;
  const avatarSize = isMobile ? 18 : 35;
  const avatarContainerSize = isMobile ? 18 : 45;
  const helpIconHeight = isMobile ? 22 : 26;

  const selectedOption = getSelectedOption();

  // Loading fallback for Suspense
  const LoadingFallback = (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        minHeight: 200,
      }}
    >
      <Spinner size={SPINNER_SIZE} color={vars.color.success.base} />
    </div>
  );

  return (
    <>
      {/* Header */}
      {shouldShowHeader && (
        <header
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: headerHeight,
            backgroundColor: vars.color.neutral.base,
            zIndex: zIndex.appShellTop,
            boxShadow: shadows.header,
            userSelect: 'none',
          }}
        >
          <div
            style={{
              position: 'relative',
              width: '100%',
              height: headerHeight,
              lineHeight: `${headerHeight}px`,
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'nowrap',
              paddingLeft: 16,
              paddingRight: headerBarRightPadding,
            }}
          >
            {/* Left section - Logo and Navigation */}
            <div
              style={{
                display: 'flex',
                flexWrap: 'nowrap',
                alignItems: 'center',
                minWidth: isMobile ? 'auto' : 310,
              }}
            >
              {/* Logo — wrapped in a real <button> so keyboard users can
                  activate it (Enter/Space) and screen readers announce it as
                  interactive. The button has no chrome of its own; the <img>
                  inside keeps the visual treatment. */}
              <button
                type="button"
                aria-label="Go to home"
                data-testid={testIds.components.appShell.logo}
                onClick={handleLogoClick}
                style={{
                  background: 'transparent',
                  border: 'none',
                  padding: 0,
                  margin: 0,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                }}
              >
                <img
                  src={headerLogoSrc}
                  alt="Ving"
                  style={{
                    height: logoHeight,
                    width: 'auto',
                    maxWidth: logoMaxWidth,
                    objectFit: 'contain',
                    marginTop: logoMarginY,
                    marginBottom: logoMarginY,
                    marginLeft: 8,
                    cursor: 'pointer',
                  }}
                />
              </button>

              {/* Navigation Options - Desktop + Admin/Manager only */}
              {showNavigation && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginLeft: 30,
                  }}
                >
                  {navOptions.map((option) => {
                    if (!option.accessCheck()) return null;

                    const isSelected = selectedOption === option.name;
                    const visibleOptions = navOptions.filter((o) => o.accessCheck());
                    const isLastVisible = visibleOptions.indexOf(option) === visibleOptions.length - 1;

                    const IconComponent = option.icon;

                    return (
                      <div
                        key={option.name}
                        onClick={() => handleNavClick(option.route)}
                        title={option.title}
                        data-testid={testIds.components.appShell.navIcon(option.name)}
                        className={isLastVisible ? navOptionClass : navOptionDividerClass}
                      >
                        <IconComponent
                          style={{
                            fontSize: 24,
                            color: isSelected ? vars.color.success.base : vars.color.fixed.white,
                            verticalAlign: 'middle',
                          }}
                        />
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Navigation Toggle - Mobile + Admin/Manager only. Opens the
                  same nav options in a Drawer since there's no room for
                  inline icons in the mobile header. */}
              {showMobileNavToggle && (
                <button
                  type="button"
                  aria-label="Open navigation menu"
                  onClick={() => setIsNavDrawerOpen(true)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    marginLeft: 12,
                    cursor: 'pointer',
                    color: vars.color.fixed.white,
                  }}
                >
                  <Icons.menu style={{ fontSize: 24 }} />
                </button>
              )}
            </div>

            {/* Right section - User area and Help */}
            <div
              style={{
                display: 'flex',
                flexWrap: 'nowrap',
                justifyContent: 'flex-end',
                alignItems: 'center',
                minWidth: isMobile ? 50 : 332,
                height: headerHeight,
                paddingRight: headerRightSectionPadding,
              }}
            >
              {/* Notifications bell — admin/manager only (same gate as the
                  former user-menu item it replaced). */}
              {/* User area - authenticated: avatar + dropdown, unauthenticated: sign in link */}
              {isAuthenticated ? (
                <UserMenu
                  onOpenChange={setIsMenuOpen}
                  isMobile={isMobile}
                  onOpenProfile={() => setIsProfileOpen(true)}
                  onOpenContextSwitcher={() => setIsContextSettingsOpen(true)}
                  contextType={currentContext?.type}
                  canSwitchContext={canSwitchContext}
                  showAdmin={isSuperuser}
                  showManage={isStaff}
                >
                  <div className={userAreaClass} data-testid={testIds.components.appShell.userMenuTrigger}>
                    {/* Avatar */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: avatarContainerSize,
                      }}
                    >
                      <Avatar
                        src={getUserAvatar()}
                        alt="Avatar"
                        style={{ width: avatarSize, height: avatarSize, fontSize: isMobile ? 10 : 14, backgroundColor: vars.color.surface.border, color: vars.color.neutral.base }}
                      >
                        {getUserInitials()}
                      </Avatar>
                    </div>

                    {/* Display name - desktop only */}
                    {!isMobile && (
                      <div
                        className="user-display-name"
                        style={{
                          maxWidth: 140,
                          fontSize: 13,
                          color: vars.color.surface.border,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          paddingLeft: 5,
                        }}
                      >
                        {getUserDisplayName()}
                      </div>
                    )}

                    {/* Dropdown arrow - desktop only */}
                    {!isMobile && (
                      <div
                        className="dropdown-icon"
                        data-testid={testIds.components.appShell.dropdownArrow}
                        data-expanded={isMenuOpen}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          color: vars.color.neutral.subtle,
                        }}
                      >
                        {isMenuOpen ? <Icons.arrowDropUp style={{ fontSize: 21 }} /> : <Icons.arrowDropDown style={{ fontSize: 21 }} />}
                      </div>
                    )}
                  </div>
                </UserMenu>
              ) : (
                <Link
                  to={signInLinkHref}
                  onClick={handleSignInLinkClick}
                  data-testid={testIds.components.appShell.signInLink}
                  className={signInPillClass}
                  style={signInPillStyle(isMobile)}
                >
                  {buttonLabels.primary.signIn}
                </Link>
              )}

              {/* Notifications — between the user menu and help, sized to match
                  the help icon. Admin/manager only. */}
              {isAuthenticated && !isViewer && !noCompany && !isConsumeSurface && (
                <NotificationBell size={helpIconHeight} />
              )}

              {/* Help link */}
              <a
                href="https://support.vingapp.com/"
                target="_blank"
                rel="noopener noreferrer"
                className={helpLinkClass}
                style={{ height: headerHeight, ...helpLinkMargins(helpIconHeight) }}
              >
                <img
                  src="/assets/images/icons/help-icon.png"
                  alt="Help"
                  style={{ height: helpIconHeight }}
                />
              </a>
            </div>
          </div>
        </header>
      )}

      {/* Profile Edit Dialog - available from any page */}
      <ProfileEditDialog
        open={isProfileOpen}
        onClose={() => {
          setIsProfileOpen(false);
          useProfileDialogStore.getState().clearAddMethodRequest();
        }}
      />


      {/* Context dialog — current org/network identity + switcher (when able) */}
      <ContextSettingsDialog
        open={isContextSettingsOpen}
        onClose={() => setIsContextSettingsOpen(false)}
      />

      {/* Forced Context Selection Modal - for admin/manager with no context selected */}
      <ForcedContextSelectionModal open={!isConsumeSurface && requiresContextSelection && ctxGatePhase === 'idle'} />

      {/* Mobile Navigation Drawer - same options as the desktop header icons */}
      <Drawer
        open={isNavDrawerOpen}
        onClose={() => setIsNavDrawerOpen(false)}
        anchor="left"
        width={260}
        aria-label="Navigation menu"
      >
        <nav style={{ display: 'flex', flexDirection: 'column', paddingTop: 8 }}>
          {navOptions.map((option) => {
            if (!option.accessCheck()) return null;
            const isSelected = selectedOption === option.name;
            const IconComponent = option.icon;
            return (
              <button
                key={option.name}
                type="button"
                data-testid={testIds.components.appShell.navIcon(option.name)}
                onClick={() => handleNavClick(option.route)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  width: '100%',
                  padding: '12px 20px',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontSize: 15,
                  fontWeight: isSelected ? 700 : 400,
                  color: isSelected ? vars.color.success.base : vars.color.neutral.dark,
                }}
              >
                <IconComponent style={{ fontSize: 22, verticalAlign: 'middle' }} />
                {option.title}
              </button>
            );
          })}
        </nav>
      </Drawer>

      {/* Content Area */}
      <main
        style={{
          position: 'absolute',
          top: shouldShowHeader ? headerHeight : 0,
          left: 0,
          right: 0,
          bottom: 0,
          overflow: 'hidden',
          zIndex: zIndex.appShell,
        }}
      >
        <LoadingZoneProvider>
          <div
            style={{
              margin: '0 auto',
              // Deliberately `isNarrowViewport` (900), NOT `isMobile` (767):
              // a viewport between those two widths still gets the full
              // desktop header/nav (see `isMobile` above), so this floor must
              // not kick in until we're actually below the width the rest of
              // the desktop layout assumes — otherwise content wider than the
              // viewport gets clipped by `overflow: hidden` above.
              minWidth: isNarrowViewport ? 'auto' : breakpoints.desktopWidth,
              height: '100%',
            }}
          >
            <Suspense fallback={LoadingFallback}>
              <FeedbackZone>
                {/* Scope crashes to the content pane: a lazy-chunk load failure or
                    an Outlet-subtree throw now lands in this boundary instead of
                    bubbling to the top-level <ErrorBoundary> and replacing the
                    whole shell. resetKey=pathname clears the error on navigation. */}
                <RouteResettingErrorBoundary>
                  <Outlet />
                </RouteResettingErrorBoundary>
              </FeedbackZone>
            </Suspense>
          </div>
        </LoadingZoneProvider>
      </main>
    </>
  );
}
